import type { SeatsPagination } from 'services/api/seats/interfaces';
import type { NotificationLastKey } from 'services/notifications/types';

import type { CompositeEntityId } from '../services/company-and-contacts/mappers.ts';
import type { MigrationCompanyId } from '../services/entities/MigrationEntities.ts';
import { normalizeToString } from '../services/entities/MigrationEntities.ts';
import type { ContentFolderType } from 'services/folders/entities.ts';

export const QueryKeys = {
  contentLibrarySectionById: (id: string) => ['library', 'sections', id],
  contentLibrarySnippetById: (id: string) => ['library', 'snippets', id],
  documentSettingsCurrencies: 'documentSettings/currencies',
  currentUser: ['/user/me'],
  currentUserVerification: ['userService', 'currentUser'],
  userBffCurrentUser: ['userService', 'bff', 'currentUser'],
  onboardingTemplates: ['onboarding', 'templates'],
  currentUserPermissions: ['/user/me/role'],
  currentAccount: 'currentAccount',
  getProposalById: (proposalId: number) => ['proposal', proposalId],
  getPersonById: (personId: number | string) => ['person', personId],
  getPersons: 'persons',
  getCompanyById: (companyId: MigrationCompanyId | undefined | null) => ['company', normalizeToString(companyId)],
  getCompanyPeople: (companyId: MigrationCompanyId | undefined | null) => ['company', normalizeToString(companyId), 'people'],
  getCompanies: ['companies'],
  getContacts: (companyId?: string) => ['contacts', { companyId }],
  getCompany: (id: MigrationCompanyId) => ['company', { id: normalizeToString(id) }],
  getProspectName: ['prospect'],
  getProspects: (offset: number, limit: number, search: string) => ['prospects', offset, limit, search],
  getDocumentList: 'listDocuments',
  accountUsers: 'accountUsers',
  getAccountUsers: (accountId: number) => ['accountUsers', accountId],
  variables: ['variables', 'proposify'],
  crmVariables: ['variables', 'crm'],
  editorAssignedCustomFields: (contentId: string) => ['customFields', 'editor', contentId],
  getSignatures: (id: string) => ['signatures', id],
  ssoConfig: 'ssoConfig',
  ssoSchema: 'ssoSchema',
  ssoRefSchema: (reference: string | undefined) => ['ssoRefSchema', reference],
  ssoSettings: 'ssoSetting',
  getProposalExpiration: (porposalId: number) => ['proposal', porposalId, 'expiration'],
  userIntegrations: ['userIntegrations'],
  userOauthData: ['userOauthData'],
  getProposalContent: (proposalId: string | number) => ['proposal', proposalId, 'content'],
  getAccountPlan: (accountId: number) => ['account', 'plan', accountId],
  getBillingInfo: (accountId: number) => ['account', accountId],
  getBillingHistory: (accountId: number, cursor: string, numPerPage: number) => [
    'account',
    accountId,
    'cursor',
    cursor,
    'numPerPage',
    numPerPage,
  ],
  foldersTree: (type: ContentFolderType) => ['folders', 'tree', type],
  fonts: ['editor-fonts'],
  featureFlag: {
    getSpecificOptionalFeature: (flag: string) => ['specificOptionalFeature', flag],
    getOptionalFeatures: 'getOptionalFeatures',
  },
  getRoles: 'roles',
  accountRoles: 'accountRoles',
  getAccountRoles: (accountId: string) => ['accountRoles', accountId],
  getRolePermissions: (accountId: string, roleId: string) => ['rolePermissions', accountId, roleId],
  getDocumentExpiryAt: (contentId: string) => ['expiry-at', contentId],
  getContentStyleById: (contentId: string) => ['getContentStyleById', contentId],
  getDocumentById: (documentId: string) => ['documents', documentId],
  getDocumentMetadataByContentId: (type: string, contentId: string) => ['documentMetadata', type, contentId],
  getDocumentApprovalState: (contentId: string, accountId: number, userId: number) => [
    'documentApprovalState',
    contentId,
    accountId,
    userId,
  ],
  getDocumentApprovalStateBase: (contentId: string) => ['documentApprovalState', contentId],
  getGridsByContentId: (contentId: string) => ['documents/grids', contentId],
  salesforceCrm: 'salesforceCrm',
  salesforceObjects: 'salesforceObjects',
  salesforceCrmPrefix: (prefix: string) => ['salesforceCrmPrefix', prefix],
  getTemplateFolders: 'templateFolders',
  getLinkedAccounts: 'linkedAccounts',
  getWorkspaceAccounts: 'workspaceAccounts',
  accountAccess: 'accountAccess',
  documentImageLibraryImages: (documentId: string) => ['/documents/document-image-library', documentId],
  getAPIClients: 'getAPIClients',
  getAPIClientsZapier: ['getAPIClients'],
  getAssetByAssetId: (assetId: string) => ['assets', assetId],
  getLocationData: ['location'],
  getBillingOptions: (accountId: number, planName: string, promoCode: string) => [
    'bookingOptions',
    accountId,
    planName,
    { promoCode: promoCode },
  ],
  validateCoupon: (accountId: number, couponCode: string) => ['validateCoupon', accountId, couponCode],
  getPrismaticJWT: 'getPrismaticJWT',
  getPrismaticCredentials: 'getPrismaticCredentials',
  getSubscription: (accountId: number) => ['getSubscription', accountId],
  getPaymentAccount: (accountId: number) => ['getPaymentAccount', accountId],
  updatePaymentAccount: (accountId: number) => ['updatePaymentAccount', accountId],
  getInvitation: (token: string) => ['getInvitation', token],
  getSeats: (accountId: number, pagination?: SeatsPagination) => [
    'getSeats',
    accountId,
    pagination?.rowsPerPage,
    pagination?.showEmpty,
    pagination?.cursor,
  ],
  unassignSeat: (accountId: number) => ['unassignSeat', accountId],
  removeSeat: (accountId: number) => ['removeSeat', accountId],
  getUserRoles: (accountId: number) => ['getUserRoles', accountId],
  getDocumentCollaborators: (documentId: string) => ['getDocumentCollaborators', documentId],
  getAutomationRules: 'getAutomationRules',
  getAutomationRulesSchema: 'getAutomationRulesSchema',
  getFlexDataTopLevelObjectTypes: 'getFlexDataTopLevelObjectTypes',
  getFlexDataLinks: (documentId: string) => ['getFlexDataLinks', documentId],
  getContentFlexDataLinks: (contentId: string) => ['getContentFlexDataLinks', contentId],
  searchFlexDataLinks: 'searchFlexDataLinks',
  searchCrmCompanies: (searchTerm?: string) => ['searchCrmCompanies', searchTerm],
  getCrmCompanyById: (id: CompositeEntityId | undefined | null) => ['getCrmCompanyById', id],
  getCrmCompaniesByContactId: (contactId: CompositeEntityId | undefined | null) => ['getCrmCompaniesByContactId', contactId],
  searchCrmContacts: (searchTerm?: string) => ['searchCrmContacts', searchTerm],
  getCrmContactById: (id: CompositeEntityId | undefined | null) => ['getCrmContactById', id],
  getCrmContactsByCompanyId: (companyId: CompositeEntityId | undefined | null) => ['getCrmContactsByCompanyId', companyId],
  duplicateProposalTemplate: 'duplicateProposalTemplate',
  getTiptapAiToken: ['getTiptapAiToken'],
  generatePdfFromDocumentByContentId: (contentId: string) => ['generatePdfFromDocumentByContentId', contentId],
  getNotificationPreferences: (userId: string | null) => ['getNotificationPreferences', userId],
  getSignees: (accountId: number, inputValue: string) => ['getSignees', accountId, inputValue],
  getNotificationsFeedByUserIdBase: (userId: string | null) => ['getNotificationsFeedByUserId', userId],
  getNotificationsFeedByUserId: (userId: string | null, params?: { lastKey?: NotificationLastKey; limit?: number }) => [
    'getNotificationsFeedByUserId',
    userId,
    params,
  ],
  getEmailTemplates: 'emailTemplates',
  getPricingCatalogItems: ['pricingCatalog', 'items'],
  getPricingCatalogFolders: ['pricingCatalog', 'folders'],
  getPermissionDefinitions: ['permissionDefinitions'],
  getSystemRoles: ['systemRoles'],
  customFields: ['customFields'],
  planBasedPersmissions: ['usersPlanPermissions'],
  usersPermissions: ['users', 'me', 'permissions'],
  verifyEmail: (token: string) => ['verifyEmail', token],
};
